import { useCallback, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageHeader, ErrorState, StatusPill } from '@ui-kit';
import { useMe } from '../../_shared/hooks/useMe';
import { useForbiddenWord } from '../hooks/useForbiddenWord';
import { useUpsertForbiddenWord } from '../hooks/useUpsertForbiddenWord';
import { useDeleteForbiddenWord } from '../hooks/useDeleteForbiddenWord';
import { ForbiddenWordFormDialog } from '../components/ForbiddenWordFormDialog';
import { DeleteConfirmDialog } from '../components/DeleteConfirmDialog';
import { TerminologyToast, useToast } from '../components/TerminologyToast';
import type { ApiError } from '../types/terminology';
import type { ForbiddenWordFormValues } from '../schema/terminologySchema';

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', padding: 'var(--spacing-3) 0', borderBottom: '1px solid var(--color-surface-border)', gap: 'var(--spacing-4)', flexWrap: 'wrap' }}>
      <dt style={{ width: 160, flexShrink: 0, fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-secondary)' }}>{label}</dt>
      <dd style={{ flex: 1, margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)', wordBreak: 'break-all' }}>{value ?? '-'}</dd>
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div aria-busy="true">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} aria-hidden="true" style={{ height: 48, margin: 'var(--spacing-1) 0', background: 'var(--color-surface-muted)', borderRadius: 'var(--radius-sm)', animation: 'pulse 1.5s ease-in-out infinite' }} />
      ))}
    </div>
  );
}

export function ForbiddenWordDetailPage() {
  const { id } = useParams<{ id: string }>();
  const wordId = id ? parseInt(id, 10) : undefined;
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const meQuery = useMe();
  const isAdmin = meQuery.data?.role === 'ROLE_ADMIN';

  const wordQuery = useForbiddenWord(wordId);
  const upsertMutation = useUpsertForbiddenWord();
  const deleteMutation = useDeleteForbiddenWord();
  const { messages, addToast, dismissToast } = useToast();

  const [formOpen, setFormOpen] = useState(false);
  const [formError, setFormError] = useState<string | undefined>();
  const [deleteOpen, setDeleteOpen] = useState(false);

  const handleFormSubmit = useCallback(
    (values: ForbiddenWordFormValues) => {
      if (!wordId) return;
      setFormError(undefined);
      upsertMutation.mutate(
        {
          id: wordId,
          body: {
            bad: values.bad,
            good: values.good,
            reason: values.reason || undefined,
            source: values.source || undefined,
            status: values.status,
          },
        },
        {
          onSuccess: () => {
            setFormOpen(false);
            setFormError(undefined);
            addToast(t('terminologyManagement.toast.forbiddenUpdated'));
          },
          onError: (err: unknown) => {
            const apiErr = err as ApiError;
            const msgKey = `terminologyManagement.error.${apiErr.code}`;
            const translated = t(msgKey);
            setFormError(translated !== msgKey ? translated : apiErr.message);
          },
        }
      );
    },
    [wordId, upsertMutation, addToast, t]
  );

  const handleDeprecate = useCallback(() => {
    if (!wordId) return;
    deleteMutation.mutate(
      { id: wordId },
      {
        onSuccess: () => {
          setDeleteOpen(false);
          addToast(t('terminologyManagement.toast.forbiddenDeprecated'));
          navigate('/admin/terminology/forbidden-words');
        },
        onError: (err: unknown) => {
          setDeleteOpen(false);
          addToast((err as ApiError)?.message ?? t('terminologyManagement.error.network'), 'error');
        },
      }
    );
  }, [wordId, deleteMutation, addToast, navigate, t]);

  const formatDate = (iso?: string | null) => {
    if (!iso) return '-';
    return new Intl.DateTimeFormat(i18n.language === 'en' ? 'en-US' : 'ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    }).format(new Date(iso));
  };

  const word = wordQuery.data;

  return (
    <div style={{ padding: 'var(--spacing-6)', maxWidth: 900, margin: '0 auto' }}>
      <PageHeader
        title={word ? word.bad : t('terminologyManagement.forbidden.form.field.bad')}
        breadcrumb={<span>{t('terminologyManagement.forbidden.list.title')}</span>}
        actions={
          <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
            <button type="button" onClick={() => navigate('/admin/terminology/forbidden-words')} style={{ padding: 'var(--spacing-2) var(--spacing-3)', background: 'var(--color-surface-elevated)', border: '1px solid var(--color-surface-border)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', cursor: 'pointer', fontFamily: 'var(--font-family-sans)', color: 'var(--color-text-secondary)' }}>
              ← {t('terminologyManagement.detail.button.back')}
            </button>
            {isAdmin && word && (
              <>
                <button type="button" onClick={() => setFormOpen(true)} style={{ padding: 'var(--spacing-2) var(--spacing-4)', background: 'var(--color-brand-primary)', color: 'var(--color-text-inverse)', border: 'none', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-family-sans)' }}>
                  {t('terminologyManagement.detail.button.edit')}
                </button>
                <button type="button" onClick={() => setDeleteOpen(true)} style={{ padding: 'var(--spacing-2) var(--spacing-4)', background: 'var(--color-danger)', color: 'var(--color-text-inverse)', border: 'none', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)', fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-family-sans)' }}>
                  {t('terminologyManagement.detail.button.deprecate')}
                </button>
              </>
            )}
          </div>
        }
      />

      {wordQuery.isLoading && <DetailSkeleton />}

      {wordQuery.isError && (
        <ErrorState
          title={t('terminologyManagement.error.FORBIDDEN-404-001')}
          message={(wordQuery.error as unknown as ApiError)?.message ?? t('terminologyManagement.error.network')}
          onRetry={() => void wordQuery.refetch()}
          actions={[{ label: t('terminologyManagement.detail.button.back'), onClick: () => navigate('/admin/terminology/forbidden-words'), variant: 'secondary' }]}
        />
      )}

      {word && !wordQuery.isError && (
        <dl style={{ margin: 0 }}>
          <DetailRow label={t('terminologyManagement.detail.field.id')} value={word.id} />
          <DetailRow label={t('terminologyManagement.forbidden.form.field.bad')} value={<strong style={{ color: 'var(--color-danger)', fontFamily: 'var(--font-family-mono)' }}>{word.bad}</strong>} />
          <DetailRow label={t('terminologyManagement.forbidden.form.field.good')} value={word.good} />
          <DetailRow label={t('terminologyManagement.forbidden.form.field.reason')} value={word.reason} />
          <DetailRow label={t('terminologyManagement.detail.field.source')} value={word.source} />
          <DetailRow label={t('terminologyManagement.detail.field.status')} value={<StatusPill tone={word.status === 'ACTIVE' ? 'success' : word.status === 'DEPRECATED' ? 'neutral' : 'warning'}>{t(`terminologyManagement.status.${word.status}`)}</StatusPill>} />
          <DetailRow label={t('terminologyManagement.detail.field.autoAdded')} value={word.autoAdded ? t('terminologyManagement.detail.autoAdded.yes') : t('terminologyManagement.detail.autoAdded.no')} />
          {word.aiTrace && <DetailRow label={t('terminologyManagement.detail.field.aiTrace')} value={<span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)' }}>{word.aiTrace}</span>} />}
          <DetailRow label={t('terminologyManagement.detail.field.createdAt')} value={formatDate(word.createdAt)} />
          <DetailRow label={t('terminologyManagement.detail.field.createdBy')} value={word.createdBy} />
          <DetailRow label={t('terminologyManagement.detail.field.updatedAt')} value={formatDate(word.updatedAt)} />
          <DetailRow label={t('terminologyManagement.detail.field.updatedBy')} value={word.updatedBy} />
        </dl>
      )}

      <ForbiddenWordFormDialog
        open={formOpen}
        editTarget={word ?? null}
        isLoading={upsertMutation.isPending}
        errorMessage={formError}
        onClose={() => { setFormOpen(false); setFormError(undefined); }}
        onSubmit={handleFormSubmit}
      />

      <DeleteConfirmDialog
        open={deleteOpen}
        name={word?.bad ?? ''}
        isLoading={deleteMutation.isPending}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDeprecate}
      />

      <TerminologyToast messages={messages} onDismiss={dismissToast} />
    </div>
  );
}
